import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { marketingPersonApi, SearchUserListItem } from '../../../modules/services/marketingPersonService';
import { useAppSelector } from '../../../modules/store/hooks';
import translate from '../../../i18n/translate';

interface AddMarketingPersonModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMarketingPersonModal: React.FC<AddMarketingPersonModalProps> = ({ open, onClose, onSuccess }) => {
  const language = useAppSelector((state) => state.auth.language);

  const [searchQuery, setSearchQuery] = useState('');
  const [usersList, setUsersList] = useState<SearchUserListItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<SearchUserListItem | null>(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [searchUsers] = marketingPersonApi.useSearchUsersListMutation();
  const [addPerson] = marketingPersonApi.useAddMarketingPersonMutation();

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setLoading(true);
    setSearched(false);
    setUsersList([]);
    setSelectedUser(null);
    try {
      const result = await searchUsers({ search: searchQuery }).unwrap();
      setUsersList(result);
      setSearched(true);
    } catch (err) {
      console.error('Search failed', err);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user: SearchUserListItem) => {
    setSelectedUser(user);
  };

  const handleCreate = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      await addPerson({
        body: { phoneNumber: selectedUser.phone },
        language: language?.split('-')?.[0] || 'en',
      }).unwrap();
      setSearchQuery('');
      setUsersList([]);
      setSelectedUser(null);
      setSearched(false);
      onSuccess();
    } catch (err) {
      console.error('Failed to add marketing person', err);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setUsersList([]);
    setSelectedUser(null);
    setSearched(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle color="warning.main">{translate('marketingPersonsAddTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <TextField
            label={translate('marketingPersonsSearch')}
            placeholder="+972501234567"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSearched(false); setSelectedUser(null); }}
            required
            fullWidth
            margin="dense"
            color="warning"
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            disabled={loading || searchQuery.length < 2}
            color="warning"
            sx={{ minWidth: 140, height: 50 }}
          >
            {loading ? <CircularProgress size={20} color="warning" /> : translate('marketingPersonsFind')}
          </Button>
        </div>

        {searched && usersList.length > 0 && (
          <List dense sx={{ maxHeight: 250, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {usersList.map((user) => (
              <ListItemButton
                key={user.id}
                selected={selectedUser?.id === user.id}
                onClick={() => handleSelectUser(user)}
                sx={{
                  '&.Mui-selected': { backgroundColor: 'rgba(252, 127, 3, 0.1)', borderLeft: '3px solid #fc7f03' },
                  '&.Mui-selected:hover': { backgroundColor: 'rgba(252, 127, 3, 0.15)' },
                }}
              >
                <ListItemIcon>
                  {selectedUser?.id === user.id ? (
                    <CheckCircleIcon color="warning" />
                  ) : (
                    <PersonIcon color="warning" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={user.name || user.phone}
                  secondary={user.name ? user.phone : `ID: ${user.id}`}
                />
                <Chip label={`ID: ${user.id}`} size="small" variant="outlined" color="warning" />
              </ListItemButton>
            ))}
          </List>
        )}

        {searched && usersList.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography>{translate('marketingPersonsSearchNoResult')}</Typography>
          </Alert>
        )}

        {selectedUser && (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography>
              {translate('marketingPersonsUserFound')}: {selectedUser.name || selectedUser.phone} (ID: {selectedUser.id})
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="warning">{translate('marketingPersonsCancel')}</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating || !selectedUser}
          color="warning"
        >
          {creating ? translate('marketingPersonsAdding') : translate('marketingPersonsAdd')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMarketingPersonModal;
